// frontend/src/hooks/useWeb3.js
import { useState, useEffect } from "react";
import web3Service from "../services/web3Service";

/**
 * Hook React pour gérer la connexion Web3
 * @returns {Object} Web3 state and methods
 * @example
 * const { address, connectWallet, isConnected } = useWeb3();
 */
export function useWeb3() {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [address, setAddress] = useState(null);
  const [network, setNetwork] = useState(null);
  const [balance, setBalance] = useState("0");
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialise le provider au chargement
  useEffect(() => {
    initWeb3();
  }, []);

  const initWeb3 = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const prov = await web3Service.initProvider();
      setProvider(prov);

      const networkInfo = await web3Service.getNetworkInfo();
      setNetwork(networkInfo);

      console.log("✅ Web3 initialisé", networkInfo);
    } catch (err) {
      console.error("❌ Erreur d'initialisation Web3:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const connectWallet = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { provider: prov, signer: sign, address: addr } = await web3Service.connectWallet();
      
      setProvider(prov);
      setSigner(sign);
      setAddress(addr);
      setIsConnected(true);

      // Récupère le solde
      const bal = await web3Service.getBalance(addr);
      setBalance(bal);

      // Récupère les infos réseau
      const networkInfo = await web3Service.getNetworkInfo();
      setNetwork(networkInfo);

      console.log("✅ Wallet connecté:", addr);
      return { provider: prov, signer: sign, address: addr };
    } catch (err) {
      console.error("❌ Erreur de connexion wallet:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectWallet = () => {
    setSigner(null);
    setAddress(null);
    setBalance("0");
    setIsConnected(false);
    console.log("✅ Wallet déconnecté");
  };

  const refreshBalance = async () => {
    if (!address) return;

    try {
      const bal = await web3Service.getBalance(address);
      setBalance(bal);
    } catch (err) {
      console.error("❌ Erreur de rafraîchissement du solde:", err);
    }
  };

  const refreshNetwork = async () => {
    try {
      const networkInfo = await web3Service.getNetworkInfo();
      setNetwork(networkInfo);
    } catch (err) {
      console.error("❌ Erreur de rafraîchissement du réseau:", err);
    }
  };

  return {
    provider,
    signer,
    address,
    network,
    balance,
    isConnected,
    isLoading,
    error,
    connectWallet,
    disconnectWallet,
    refreshBalance,
    refreshNetwork,
  };
}
